import { ExpenseType } from "@/types/expenseType";
import { Button, Col, Grid, Form, Row, message, Modal, Flex } from "antd";
import { useEffect, useState } from "react";
import {
  ExpenseDraftFormValue,
  OperationFlags,
} from "../../types/expenseDraft";
import { useAuth } from "@/context/AuthContext";

import { useGetExpenseHistories } from "../../hooks/useExpenses";
import { useExpenseDescription } from "../../hooks/useExpenseDescription";
import { useExpenseSupplier } from "../../hooks/useExpenseSupplier";
import { PREFIX } from "../../constants";
import { getModalConfig } from "../../utils/expenseDraftModal";
import { getFlag } from "../../utils/expenseDraftModal";
import {
  getColProps,
  getLastExpenseDraftDate,
  getLastExpenseDraft,
  getOffset,
} from "../../utils/expenseDrafts";
import { useGetInputs } from "../../hooks/useExpenseDraftForm";
import {
  useCreateExpenseDraft,
  useGetExpenseDrafts,
} from "../../hooks/useExpenseDrafts";
import { Expense } from "@/types/expense";
import { UpdateExpenseDescriptionFormValues } from "@/pages/Expenses/ManageExpense/api/expenseDescriptions";
import { useUpdateExpenseDescription } from "@/pages/Expenses/ManageExpense/hooks/useExpenseDescriptions";

interface ExpenseDraftFormProps {
  expenseType?: ExpenseType;
}

const ExpenseDraftForm = ({ expenseType }: ExpenseDraftFormProps) => {
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const breakpoint = Grid.useBreakpoint();
  const [lastExpense, setLastExpense] =
    useState<Pick<Expense, "unit_id" | "quantity" | "price" | "supplier_id">>();
  const [form] = Form.useForm<ExpenseDraftFormValue>();

  // Used to store updates for the expense description, specifically when the description's unit_id is not set and needs to be updated
  const [descriptionUpdates, setDescriptionUpdates] =
    useState<UpdateExpenseDescriptionFormValues>();

  const descriptionId = Form.useWatch("description_id", form);
  const visibleFields = expenseType?.required_fields?.length || 0;

  const expenseDrafts = useGetExpenseDrafts({
    config: {
      params: {
        limit: -1,
        sort: { field: "created_at", order: "desc" },
        user_id_eq: user?.id,
      },
    },
  });
  const expenseHistory = useGetExpenseHistories({
    config: {
      params: {
        limit: 1,
        sort: { field: "created_at", order: "desc" },
        filters: [
          { field: "expense_type_id", operator: "eq", value: expenseType?.id },
          {
            field: "description_id",
            operator: "eq",
            value: descriptionId?.replace(PREFIX.INACTIVE, ""),
          },
        ],
      },
    },
    staleTime: 0,
    gcTime: 0,
    enabled:
      !!expenseType?.id &&
      !!descriptionId &&
      !getFlag(descriptionId, PREFIX.NEW),
  });

  const inputs = useGetInputs({
    form,
    breakpoint,
    expenseType,
    lastExpenseDate: getLastExpenseDraftDate(expenseDrafts?.data || []),
    setDescriptionUpdates,
  });

  const { mutate: createDraft } = useCreateExpenseDraft();
  const { mutate: updateExpenseDescription } = useUpdateExpenseDescription();
  const { handleAddExpenseDescription, handleReactivateExpenseDescription } =
    useExpenseDescription();
  const { handleAddExpenseSupplier, handleReactivateExpenseSupplier } =
    useExpenseSupplier();

  // handle adding expense to drafts with the flags
  const handleAddToDrafts = async (
    values: ExpenseDraftFormValue,
    flags: OperationFlags
  ) => {
    const onCloseConfirmatioModal = () => {
      setSubmitting(false);
      Modal.destroyAll();
    };

    // Create a working copy of values for processing
    let workingValues = { ...values };

    if (flags.isNewDescription) {
      await handleAddExpenseDescription({
        values: workingValues,
        onSuccess: (description) => {
          workingValues.description_id = description.id;
        },
        onError: () => {
          onCloseConfirmatioModal();
        },
      });
    } else if (flags.isReactivateDescription) {
      await handleReactivateExpenseDescription({
        values: workingValues,
        onSuccess: (description) => {
          workingValues.description_id = description.id;
        },
        onError: () => {
          onCloseConfirmatioModal();
        },
      });
    }

    if (flags.isNewSupplier) {
      await handleAddExpenseSupplier({
        values: workingValues,
        onSuccess: (supplier) => {
          workingValues.supplier_id = supplier.id;
        },
        onError: () => {
          onCloseConfirmatioModal();
        },
      });
    } else if (flags.isReactivateSupplier) {
      await handleReactivateExpenseSupplier({
        values: workingValues,
        onSuccess: (supplier) => {
          workingValues.supplier_id = supplier.id;
        },
        onError: () => {
          onCloseConfirmatioModal();
        },
      });
    }

    if (descriptionUpdates) {
      updateExpenseDescription(descriptionUpdates, {
        onSuccess: () => {
          message.success("Successfully updated expense description");
          onCloseConfirmatioModal();
        },
        onError: () => {
          onCloseConfirmatioModal();
        },
      });
    }

    // Create draft via API
    createDraft(workingValues, {
      onSuccess: () => {
        form.resetFields([
          "expense_type_id",
          "description_id",
          "supplier_id",
          "price",
          "quantity",
          "unit_id",
          "staff_name",
        ]);
        message.success("Successfully added expense to drafts");
        setSubmitting(false);
      },
      onError: () => {
        setSubmitting(false);
      },
    });
  };

  // handle the form submission
  const handleFinish = async (values: ExpenseDraftFormValue) => {
    setSubmitting(true);

    if (!user) {
      message.error("User not found, please login again");
      return;
    }

    const newValues: ExpenseDraftFormValue = {
      ...values,
      expense_type_id: expenseType?.id || "",
      user_id: user.id,
    };

    const flags: OperationFlags = {
      isNewDescription: getFlag(newValues.description_id, PREFIX.NEW),
      isNewSupplier: getFlag(newValues.supplier_id, PREFIX.NEW),
      isReactivateDescription: getFlag(
        newValues.description_id,
        PREFIX.INACTIVE
      ),
      isReactivateSupplier: getFlag(newValues.supplier_id, PREFIX.INACTIVE),
    };

    const modalConfig = getModalConfig(flags);

    if (modalConfig) {
      Modal.confirm({
        title: modalConfig.title,
        content: modalConfig.content,
        okText: "Yes",
        cancelText: "Cancel",
        onOk: () => handleAddToDrafts(newValues, flags),
        onCancel: () => setSubmitting(false),
      });
      return;
    }

    handleAddToDrafts(newValues, flags);
  };

  // set is ready true when the form is already filled with the required fields
  const onValuesChange = () => {
    if (!expenseType?.required_fields) {
      return;
    }

    const fields = Object.keys(form.getFieldsValue());
    const formValues = form.getFieldsValue();

    const isReady = fields.every((field) => {
      if (field === "empty") {
        return true;
      }

      return !!formValues[field as keyof ExpenseDraftFormValue];
    });

    setIsReady(isReady);
  };

  useEffect(() => {
    setIsReady(false);
    onValuesChange();
    form.resetFields([
      "expense_type_id",
      "description_id",
      "supplier_id",
      "price",
      "quantity",
      "unit_id",
      "staff_name",
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenseType, form]);

  // set last expense based on history or draft
  useEffect(() => {
    if (expenseHistory.data && expenseHistory.data.length > 0) {
      const lastExpenseHistory = expenseHistory.data[0];
      const validExpenseData = {
        quantity: lastExpenseHistory.quantity,
        price: lastExpenseHistory.price,
        supplier_id: lastExpenseHistory.supplier?.is_active
          ? lastExpenseHistory.supplier_id
          : undefined,
        unit_id: lastExpenseHistory.unit_id,
      };
      setLastExpense(validExpenseData);
    } else {
      const lastExpenseDraft = getLastExpenseDraft({
        expenseDrafts: expenseDrafts.data,
        expense_type_id: expenseType?.id,
        description_id: descriptionId,
      });
      setLastExpense(lastExpenseDraft);
    }
  }, [expenseHistory.data, expenseDrafts.data, descriptionId, expenseType?.id]);

  // auto select Unit based on last expense unit by description
  useEffect(() => {
    if (lastExpense) {
      form.setFieldsValue({
        quantity: lastExpense.quantity,
        price: lastExpense.price,
        supplier_id: lastExpense.supplier_id,
        unit_id: lastExpense.unit_id,
      });
    } else {
      form.setFieldsValue({
        quantity: undefined,
        price: undefined,
        supplier_id: undefined,
        unit_id: undefined,
      });
    }
    onValuesChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastExpense, form]);

  return (
    <Form<ExpenseDraftFormValue>
      form={form}
      onFinish={handleFinish}
      layout="vertical"
      style={{
        width: breakpoint.xl ? "70%" : "100%",
      }}
      hidden={!expenseType}
      onValuesChange={onValuesChange}
    >
      <Row gutter={[16, 16]}>
        {inputs.map((input, index) => (
          <Col
            key={input.itemProps.name + index}
            {...getColProps(visibleFields)}
          >
            <Form.Item
              {...input.itemProps}
              style={{ marginBottom: 0, maxWidth: 500 }}
              labelCol={{ style: { paddingBottom: 4 } }}
            >
              {input.component}
            </Form.Item>
          </Col>
        ))}
        <Col
          {...getColProps(visibleFields)}
          offset={getOffset({ breakpoint, visibleFields })}
          key="button"
        >
          <Flex
            style={{
              width: "100%",
              height: "100%",
              alignItems: "end",
              justifyContent: "end",
            }}
          >
            <Button
              htmlType="submit"
              type="default"
              variant="solid"
              color="primary"
              loading={submitting}
              disabled={!isReady}
              style={{
                width: breakpoint.xs ? "auto" : "100%",
              }}
            >
              Add to Draft
            </Button>
          </Flex>
        </Col>
      </Row>
    </Form>
  );
};

export default ExpenseDraftForm;
